import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SortableTable from './components/SortableTable';
import { httpClient, publicBase } from './dataProvider';

export const TrainingAccessRequestList = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, pending, approved, denied

    const fetchRequests = () => {
        const statusParam = filter !== 'all' ? `&status=${filter}` : '';
        const url = `${publicBase}/admin/training-access-requests?limit=1000${statusParam}`;
        httpClient(url)
            .then(({ json }) => {
                setRequests(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching training access requests:', error);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchRequests();
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApprove = async (record) => {
        if (window.confirm(`Approve training access for ${record.email}?`)) {
            try {
                await httpClient(`${publicBase}/admin/training-access-requests/${record.id}/approve`, {
                    method: 'POST'
                });
                alert('Access request approved and user added to approved list!');
                fetchRequests(); // Refresh the list
            } catch (error) {
                console.error('Error approving request:', error);
                alert('Failed to approve request');
            }
        }
    };

    const handleUpdateStatus = async (record, newStatus) => {
        try {
            await httpClient(`${publicBase}/admin/training-access-requests/${record.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    status: newStatus,
                    followed_up_at: true
                })
            });
            fetchRequests(); // Refresh the list
        } catch (error) {
            console.error('Error updating request:', error);
            alert('Failed to update request status');
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            pending: '#ff9800',
            approved: '#4caf50',
            denied: '#f44336'
        };

        return (
            <span style={{
                padding: '4px 8px',
                backgroundColor: colors[status] || '#666',
                color: 'white',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
            }}>
                {status.toUpperCase()}
            </span>
        );
    };

    const getRecaptchaScore = (score) => {
        if (score === null || score === undefined) return 'N/A';

        const color = score >= 0.7 ? '#4caf50' : score >= 0.3 ? '#ff9800' : '#f44336';
        return (
            <span style={{ color, fontWeight: 'bold' }}>
                {score.toFixed(2)}
            </span>
        );
    };

    const columns = [
        { key: 'email', header: 'Email' },
        {
            key: 'requested_at',
            header: 'Requested',
            render: (record) => new Date(record.requested_at).toLocaleString()
        },
        {
            key: 'status',
            header: 'Status',
            render: (record) => getStatusBadge(record.status)
        },
        {
            key: 'recaptcha_score',
            header: 'Security Score',
            render: (record) => getRecaptchaScore(record.recaptcha_score)
        },
        { key: 'ip_address', header: 'IP Address' },
        {
            key: 'followed_up_at',
            header: 'Followed Up',
            render: (record) => record.followed_up_at ?
                new Date(record.followed_up_at).toLocaleDateString() : 'No'
        },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {record.status === 'pending' && (
                        <>
                            <button
                                onClick={() => handleApprove(record)}
                                style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Approve
                            </button>
                            <button
                                onClick={() => handleUpdateStatus(record, 'denied')}
                                style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Deny
                            </button>
                        </>
                    )}
                    <Link
                        to={`/training-access-requests/${record.id}`}
                        style={{
                            padding: '4px 8px',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontSize: '12px'
                        }}
                    >
                        Details
                    </Link>
                </div>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Training Access Requests</h2>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <label>Filter by status:</label>
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        style={{
                            padding: '4px 8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                    >
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="denied">Denied</option>
                    </select>
                </div>
            </div>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <strong>Security Score Guide:</strong>
                <span style={{ marginLeft: '12px', color: '#4caf50' }}>0.7+ = Likely Human</span>
                <span style={{ marginLeft: '12px', color: '#ff9800' }}>0.3-0.7 = Uncertain</span>
                <span style={{ marginLeft: '12px', color: '#f44336' }}>0.3- = Likely Bot</span>
            </div>

            <SortableTable
                data={requests}
                columns={columns}
                defaultSort={{ key: 'requested_at', direction: 'desc' }}
            />
        </div>
    );
};

export const TrainingAccessRequestDetail = ({ match }) => {
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState('');

    const requestId = match?.params?.id;

    useEffect(() => {
        if (requestId) {
            // For now, we'll fetch from the list and find the specific request
            // In a real implementation, you'd have a detail endpoint
            const url = `${publicBase}/admin/training-access-requests?limit=1000`;
            httpClient(url)
                .then(({ json }) => {
                    const foundRequest = json.find(r => r.id === requestId);
                    setRequest(foundRequest);
                    setNotes(foundRequest?.notes || '');
                    setLoading(false);
                })
                .catch(error => {
                    console.error('Error fetching request details:', error);
                    setLoading(false);
                });
        }
    }, [requestId]);

    const handleUpdateNotes = async () => {
        try {
            await httpClient(`${publicBase}/admin/training-access-requests/${requestId}`, {
                method: 'PUT',
                body: JSON.stringify({ notes })
            });
            alert('Notes updated successfully!');
        } catch (error) {
            console.error('Error updating notes:', error);
            alert('Failed to update notes');
        }
    };

    if (loading) return <p>Loading...</p>;
    if (!request) return <p>Request not found</p>;

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Training Access Request Details</h2>
                <Link
                    to="/training-access-requests"
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#666',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px'
                    }}
                >
                    Back to List
                </Link>
            </div>

            <div style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                        <strong>Email:</strong> {request.email}
                    </div>
                    <div>
                        <strong>Status:</strong> {request.status}
                    </div>
                    <div>
                        <strong>Requested:</strong> {new Date(request.requested_at).toLocaleString()}
                    </div>
                    <div>
                        <strong>Security Score:</strong> {request.recaptcha_score?.toFixed(2) || 'N/A'}
                    </div>
                    <div>
                        <strong>IP Address:</strong> {request.ip_address || 'N/A'}
                    </div>
                    <div>
                        <strong>Followed Up:</strong> {request.followed_up_at ?
                            new Date(request.followed_up_at).toLocaleString() : 'No'}
                    </div>
                </div>

                {request.user_agent && (
                    <div style={{ marginTop: '16px' }}>
                        <strong>User Agent:</strong>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {request.user_agent}
                        </div>
                    </div>
                )}
            </div>

            <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Admin Notes:
                </label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        resize: 'vertical'
                    }}
                    placeholder="Add notes about follow-up actions, decisions, etc..."
                />
                <button
                    onClick={handleUpdateNotes}
                    style={{
                        marginTop: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Update Notes
                </button>
            </div>
        </div>
    );
};
